import React from "react";
import { ModalProps } from "react-bootstrap/Modal";
import { useSetAtom } from "jotai";
import { Image } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import "./HprInfoModal.scss";
import CloseRoundedIcon from "../../../icons/CloseRoundedIcon";
import HyperCoin from "../../../HyperCoin/HyperCoin";
import WalletOpenIcon from "../../../icons/WalletOpenIcon";
import AxeIcon from "../../../icons/AxeIcon";
import HandHoldingDollarIcon from "../../../icons/HandHoldingDollarIcon";
import GhostButton from "../../GhostButton/GhostButton";
import { formatCurrency } from "../../../../utils/helpers";
import FixedFooter from "../../../FixedFooter/FixedFooter";
import FilledButton, { ButtonSizes } from "../../FilledButton/FilledButton";
import { HandleTopup } from "../../../topup/TopupFlow/types";
import DownloadAppPrompt from "../../../DownloadAppPrompt/DownloadAppPrompt";
import useDisclosure from "../../../../hooks/useDisclosure";
import useAuth from "../../../../hooks/useAuth";
import {
  initiateTopupTriggerAtom,
  InitiateTopupTriggers,
} from "../../../../jotai/atoms/analytics";
import ImageHyperCoinStack from "../../../../images/bonus-hyper-coin-stack.svg";

export function HowToGetHpr({
  showSteps = [true, true, true],
}: {
  showSteps?: boolean[];
}) {
  const { t } = useTranslation("dashboard");
  return (
    <div className="how-to-get-hpr">
      <h2>
        {t("how_to_get")}{" "}
        <Image src={ImageHyperCoinStack} alt="HPR" width={22.7} height={14} />{" "}
        HPR
      </h2>
      {showSteps[0] && (
        <div className="item">
          <div className="col-one">
            <div className="icon-container bg-green">
              <span className="coin-wallet">
                <HyperCoin className="coin" />
                <WalletOpenIcon fill="#389E0D" className="wallet" />
              </span>
            </div>
          </div>
          <div className="col-two">
            <h5>{t("purchase")} HPR</h5>
            <p className="content">
              {t("purchase_an_hpr_bundle_via_the_wallet_page", { hpr: "HPR" })}.
            </p>
          </div>
        </div>
      )}
      {showSteps[1] && (
        <div className="item">
          <div className="col-one">
            <div className="icon-container">
              <AxeIcon fill="#509BF5" />
            </div>
          </div>
          <div className="col-two">
            <h5>{t("win_1v1_and_multiplayer_tournaments")}</h5>
            <p className="content">
              {t(
                "play_games_with_entry_fees_and_win_the_victory_pot_to_earn_more"
              )}{" "}
              <Image
                src={ImageHyperCoinStack}
                alt="HPR"
                width={22.7}
                height={14}
              />{" "}
              HPR.
            </p>
          </div>
        </div>
      )}
      {showSteps[2] && (
        <div className="item" style={{ marginBottom: 0 }}>
          <div className="col-one">
            <div className="icon-container">
              <HandHoldingDollarIcon fill="#AB66FF" />
            </div>
          </div>
          <div className="col-two">
            <h5>{t("cash_out")}!</h5>
            <p className="content">
              {t("convert_your_hpr_into_cash_easily", { hpr: "HPR" })}. 💰
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

interface HprInfoModalProps extends ModalProps {
  balance?: number;
  handleTopup: HandleTopup;
}

function HprInfoModal({
  onHide,
  show,
  balance,
  handleTopup,
}: HprInfoModalProps) {
  const { t } = useTranslation("dashboard");
  const balanceText = balance === undefined ? "--" : formatCurrency(balance);
  const { isLoggedIn } = useAuth();
  const setInitiateTopupTrigger = useSetAtom(initiateTopupTriggerAtom);
  const {
    isOpen: isDownloadAppPromptOpen,
    onOpen: openDownloadAppPrompt,
    onClose: closeDownloadAppPrompt,
  } = useDisclosure();

  const handlePurchaseHpr = () => {
    if (isLoggedIn) {
      setInitiateTopupTrigger(InitiateTopupTriggers.HPR_INFO_MODAL);
      handleTopup();
    }
    openDownloadAppPrompt();
  };

  if (!show) return null;

  return (
    <div className="fixed-modal MobileOnly" role="dialog" aria-modal="true">
      <div className="hero">
        <p className="close-btn-container d-flex justify-content-end">
          <button aria-label="close" onClick={onHide}>
            <CloseRoundedIcon fill="white" />
          </button>
        </p>
        <p className="d-flex justify-content-center">
          <span className="balance">
            <HyperCoin width={25} height={25} />
            <span>{balanceText}</span>
          </span>
        </p>
        <p className="hero-text">
          {t(
            "hpr_are_real_money_rewards_you_earn_from_winning_battle_games_and_multiplayer_tournament",
            { hpr: "HPR" }
          )}
          .
        </p>
      </div>
      <div className="info-container">
        <HowToGetHpr />
        <FixedFooter className="px-3 pb-4">
          <FilledButton
            onClick={onHide}
            style={{ background: "#6c53e8", color: "white" }}
            fullwidth
            size={ButtonSizes.MD}
          >
            ({t("common:got_it")})
          </FilledButton>
          <GhostButton className="purchase-btn" onClick={handlePurchaseHpr}>
            {t("purchase")} HPR
          </GhostButton>
        </FixedFooter>
      </div>

      <DownloadAppPrompt
        isOpen={isDownloadAppPromptOpen}
        onOpen={openDownloadAppPrompt}
        onClose={closeDownloadAppPrompt}
      />
    </div>
  );
}

export default HprInfoModal;
